import React, { useRef, useEffect } from "react";
import {
  Chart as ChartJS,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

import {
  severityColors,
  severityCode,
} from "../../../Maps/components/Severity/severityUtils";
import { Box, Typography } from "@mui/material";

const PyramidChart = ({ dataSource, handleSeveritySelected }) => {
  const canvasRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const explanations = {
    Major: "Volume >= 10,000 barrels",
    Moderate: "Volume >= 1,000 barrels < 10,000 barrels ",
    Minor: "Volume < 1000 barrels",
  };

  useEffect(() => {
    if (!canvasRef.current || !dataSource || dataSource.length === 0) {
      return;
    }

    try {
      const ctx = canvasRef.current.getContext("2d");
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
      const data = {
        labels: dataSource.map(
          (dataPoint) => dataPoint.severity || dataPoint.state
        ),
        datasets: [
          {
            label: "Count",
            backgroundColor: dataSource.map(
              (dataPoint) =>
                severityColors[dataPoint.severity] ||
                severityColors[dataPoint.state]
            ),
            data: dataSource.map((dataPoint) => dataPoint.count),
          },
          {
            label: "Count",
            backgroundColor: dataSource.map(
              (dataPoint) =>
                severityColors[dataPoint.severity] ||
                severityColors[dataPoint.state]
            ),
            data: dataSource.map((dataPoint) => -dataPoint.count),
          },
        ],
      };

      chartInstanceRef.current = new ChartJS(ctx, {
        type: "bar",
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.dataset.label || "";
                  const value = Math.abs(context.raw);
                  const severity = context.label;
                  const explanation = explanations[severity] || "";
                  return `${label}: ${value} (${severity}) - ${explanation}`;
                },
              },
            },
            legend: {
              display: false,
            },
          },
          indexAxis: "y",
          scales: {
            x: {
              stacked: true,
              ticks: {
                callback: (value) => Math.abs(value),
              },
            },
            y: {
              stacked: true,
              ticks: {
                beginAtZero: true,
              },
            },
          },
          onClick: (_, elements) => {
            if (elements.length > 0) {
              const element = elements[0];
              let severity = data.labels[element.index];
              if (severity) {
                handleSeveritySelected(
                  severityCode[severity]
                );
              } else {
                handleSeveritySelected(null);
              }
            }
          },
        },
      });
    } catch (error) {
      console.error("Error al generar la gráfica:", error);
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [dataSource]);

  if (!dataSource || dataSource.length === 0) {
    return (
      <Box
        width={"100%"}
        flexGrow={1}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        maxHeight={"30vh"}
        minHeight={"30vh"}
      >
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      width={"100%"}
      flexGrow={1}
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
      maxHeight={"30vh"}
      minHeight={"30vh"}
    >
      <canvas ref={canvasRef}></canvas>
    </Box>
  );
};

export default PyramidChart;
